import classNames from 'classnames';
import { Routes } from '@/routes';
import { CgMore } from 'react-icons/cg';
import { Empty, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDevice, useHelpdesk } from '@/hooks';
import { ColumnBuilder, formatDate } from '@/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, LoadingSpin, RetryAlert, LogTable, LogTableColumn, PageContainer } from '@/components/common';
import { statusColor } from '@/components/helpdesk/helpdeskUtils';
import HelpdeskItemMobile from './HelpdeskItemMobile';

const { Option } = Select;

export default function HelpdeskIssueList() {

  const pageSize = 100;
  const [t] = useTranslation(['common', 'helpdesk']);

  const columns = useMemo(() => {
    return ColumnBuilder.create([
      { key: 'id', title: t('common:labels.id'), width: 100 },
      { key: 'subject', title: t('common:labels.title') },
      { key: 'type', title: t('helpdesk:labels.request-type'), width: 175 }, // actually it is project.name
      {
        key: 'status',
        title: t('common:labels.status'),
        width: 200,
        align: 'right',
        render: (name: string) => (
          <Tag
            color={statusColor[name]}
            className="!rounded-full !text-sm !px-2.5 !py-1 font-medium text-white"
          >{name}</Tag>
        )
      },
      { key: 'createdAt', width: 150, title: t('common:labels.created-at') },
      { key: 'updatedAt', width: 150, title: t('common:labels.updated-at') },
      { key: 'action', title: ' ', align: 'center' }
    ]).build() as LogTableColumn[];
  }, [t]);

  const { isDesktop } = useDevice();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: -1 });
  const {
    issues,
    issuesError,
    issuesLoading,
    fetchIssues,
    statuses,
    fetchStatuses,
    statusesError,
    statusesLoading,
  } = useHelpdesk();

  const handleStatusFilterChange = useCallback((value: any) => {
    setFilters({ status: Number(value) });
  }, [setFilters]);

  useEffect(() => {
    fetchStatuses();
    fetchIssues(1, pageSize);
  }, []);

  const renderedFilterComponents = useMemo(() => (
    <Flex>
      <Select
        size="large"
        className={classNames('text-[15px]', isDesktop() ? 'w-64' : 'w-full')}
        placeholder={t('common:labels.type')}
        onChange={handleStatusFilterChange}>
        <Option value={-1}> {t('common:labels.all')}</Option>
        {statuses.map(iss => (
          <Option key={iss.id} value={iss.id}>{iss.name}</Option>
        ))}
      </Select>
    </Flex>
  ), [handleStatusFilterChange, statuses, isDesktop]);

  // Mobile mode use this original filtered data
  const filtered = (issues ?? []).filter(x => filters.status === -1 || x.status.id === filters.status);

  // Desktop mode use mapped data in table
  const tableRows = filtered.map(iss => ({
    id: iss.id,
    subject: iss.subject,
    type: iss.project?.name,
    status: iss.status?.name,
    createdAt: formatDate(iss.created_on, 'jYYYY/jMM/jDD'),
    updatedAt: formatDate(iss.updated_on, 'jYYYY/jMM/jDD'),
    action: (
      <Flex
        inlineFlex
        align="center"
        justify="center"
        onClick={() => navigate(Routes.HelpdeskIssues + '/' + iss.id)}
        className="p-1 rounded-lg cursor-pointer hover:bg-gray-200">
        <CgMore style={{ fontSize: '1.3rem' }} />
      </Flex>
    )
  }));

  if (issuesError || statusesError) {
    return (
      <PageContainer>
        <Flex justify="center">
          <RetryAlert noIcon action={() => {
            fetchStatuses();
            fetchIssues(1, pageSize);
          }} />
        </Flex>
      </PageContainer>
    );
  }

  const loading = issuesLoading || statusesLoading;

  return (
    <PageContainer title={t('common:labels.helpdesk')}>
      <Flex
        direction="column"
        className="min-h-full"
        justify={!isDesktop() && loading ? 'center' : 'flex-start'}
      >

        {isDesktop() ?
          <>
            {renderedFilterComponents}
            <LogTable
              rows={tableRows}
              columns={columns}
              loading={loading}
              pagination={false}
            />
          </> :
          loading ? <LoadingSpin className="text-helpdesk" fillHeight /> : (
            <>
              {renderedFilterComponents}
              {filtered.length === 0 ? (<Empty className="my-auto" />) :
                filtered.map((item) => (
                  <HelpdeskItemMobile key={item.id} {...item} />
                ))
              }
            </>
          )
        }
      </Flex>
    </PageContainer>
  );
}
